import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewPaymentTermModal from 'components/NewPaymentTermModal';
import { CANCELLATION_CATEGORY, getCancellationTypeLabel, hasValue } from 'utils/paymentTermConstants';

const PaymentTerms = () => {
    const [terms, setTerms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('payment_terms');

    useEffect(() => {
        fetchTerms();
    }, []);

    const fetchTerms = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/payment-terms/');
            setTerms(response.data);
        } catch (error) {
            console.error('Error fetching payment terms:', error);
            setSnackbar({ open: true, message: 'Error loading payment terms', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveTerm = async (payload) => {
        try {
            const { refund_term, ...rest } = payload;
            const finalPayload = payload.category === CANCELLATION_CATEGORY ? rest : payload;
            await axiosInstance.post('/payment-terms/', finalPayload);
            setSnackbar({ open: true, message: 'Payment Term created successfully', severity: 'success' });
            fetchTerms();
        } catch (error) {
            console.error('Error saving payment term:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/payment-term/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "16%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Payment Due",
            accessor: "payment_due",
            width: "12%"
        },
        {
            Header: "Payment Type",
            accessor: "payment_type",
            width: "9%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Days",
            accessor: "days",
            width: "6%",
            Cell: ({ value }) => hasValue(value) ? value : '-'
        },
        {
            Header: "Amount (%)",
            accessor: "amount_percent",
            width: "8%",
            Cell: ({ value }) => hasValue(value) ? `${value}%` : '-'
        },
        {
            Header: "Amount ($)",
            accessor: "amount_actual",
            width: "8%",
            // Cancellation terms are shown without a currency symbol
            Cell: ({ value, row }) => {
                if (!hasValue(value)) return '-';
                return row.original.category === CANCELLATION_CATEGORY ? value : `$${value}`;
            }
        },
        {
            Header: "Category",
            accessor: "category",
            width: "9%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Cancellation Type",
            accessor: "cancellation_type",
            width: "9%",
            Cell: ({ value }) => value ? getCancellationTypeLabel(value) : '-'
        },
        {
            Header: "Refund Method",
            accessor: "refund_method",
            width: "9%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Status",
            accessor: "status",
            width: "7%",
            Cell: ({ value }) => value || '-'
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "9%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: terms,
    }), [terms]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Payment Terms</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Payment Term
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : terms.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Payment Term" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>


            <NewPaymentTermModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveTerm}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default PaymentTerms;